import { Component, DestroyRef, inject, input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { ButtonAddNewComponent } from '../../../../shared/components/button-add-new/button-add-new.component';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { CaracteristiqueCreateRequest } from '../requests/caractéristique-create-request';
import { CaractéristiqueStore } from '../store/caractéristique.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-caracteristiques',
  imports: [ReactiveFormsModule, HlmFieldImports, HlmInputImports, ButtonAddNewComponent, LabelRequiredDirective],
  templateUrl: './form-caracteristiques.component.html',
})
export class FormCaracteristiquesComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #caracteristiqueStore = inject(CaractéristiqueStore);

  readonly referentiel = input.required<Référentiel>();

  readonly form = this.#fb.nonNullable.group({
    caracteristiques: this.#fb.array([]),
  });

  ngOnInit(): void {
    this.addCaracteristique();

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  addCaracteristique() {
    const caracteristique = this.#fb.nonNullable.group({
      nom: [null, Validators.required],
    });
    this.caracteristiques.push(caracteristique);
  }

  onSubmit() {
    const toastrId = uuidv7();

    const nouvellesCaracteristiques: CaracteristiqueCreateRequest = {
      idReferentiel: this.referentiel().id,
      caracteristiques: (this.caracteristiques.value as any[])
        .filter((x: { nom: string }) => !!x.nom?.trim())
        .map((x) => ({ nom: x.nom, estSecteur: false })),
    };

    if (!nouvellesCaracteristiques.caracteristiques || nouvellesCaracteristiques.caracteristiques.length === 0) {
      this.form.markAllAsTouched();
      return;
    }

    this.#caracteristiqueStore.créer({
      data: nouvellesCaracteristiques,
      next: () => {
        this.#caracteristiqueStore.charger(this.referentiel().id);
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Création des caractéristiques réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(nouvellesCaracteristiques.caracteristiques),
    });
  }

  alreadyExists(
    nouvellesCaractéristiques: {
      nom: string;
    }[],
  ) {
    const toutesLesCaractéristiques = this.#caracteristiqueStore.caracteristiques().map((x) => x.nom);

    const doublonsAvecCaractéristiquesExistantes = toutesLesCaractéristiques.filter((x) =>
      nouvellesCaractéristiques.map((y) => y.nom).includes(x),
    );

    this.caracteristiques.controls.forEach((control) => {
      if (
        doublonsAvecCaractéristiquesExistantes.includes(control.value.nom) ||
        this.caracteristiques.controls.find((c) => c.value.nom === control.value.nom && c != control)
      ) {
        control.get('nom')?.setErrors({ notUnique: true });
      }
    });
  }

  get caracteristiques() {
    return this.form.get('caracteristiques') as FormArray;
  }
}
